import { useAuth0 } from '@auth0/auth0-react';
import { onTokenRefreshed } from '@sunstone/api-client';
import { useEffect, useState } from 'react';
import { useIsServer } from '@/app/ServerContext';

/**
 * Custom hook to get the Auth0 access token for API calls.
 * Automatically includes the audience to get a JWT token.
 * Returns loading state during SSR.
 */
export const useAuthToken = () => {
  const isServer = useIsServer();

  if (isServer) {
    return { token: null, tokenError: null, isLoading: true };
  }

  // biome-ignore lint/correctness/useHookAtTopLevel: Intentional SSR guard — isServer is stable and this hook is only called on the client
  const { getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0();
  // biome-ignore lint/correctness/useHookAtTopLevel: Intentional SSR guard — isServer is stable and this hook is only called on the client
  const [token, setToken] = useState<string | null>(null);
  // biome-ignore lint/correctness/useHookAtTopLevel: Intentional SSR guard — isServer is stable and this hook is only called on the client
  const [tokenError, setTokenError] = useState<Error | null>(null);

  // biome-ignore lint/correctness/useHookAtTopLevel: Intentional SSR guard — isServer is stable and this hook is only called on the client
  useEffect(() => {
    const getToken = async () => {
      if (!isAuthenticated || isLoading) {
        return;
      }

      try {
        const accessToken = await getAccessTokenSilently();
        if (!accessToken) {
          throw new Error('Auth0 returned no access token');
        }
        setToken(accessToken);
        setTokenError(null);
      } catch (error) {
        console.error('Error getting access token:', error);
        setTokenError(error as Error);
      }
    };

    getToken();
  }, [isAuthenticated, isLoading, getAccessTokenSilently]);

  // biome-ignore lint/correctness/useHookAtTopLevel: Intentional SSR guard — isServer is stable and this hook is only called on the client
  useEffect(() => {
    return onTokenRefreshed((freshToken) => {
      setToken(freshToken);
      setTokenError(null);
    });
  }, []);

  return {
    token,
    tokenError,
    isLoading: isLoading || (isAuthenticated && !token && !tokenError),
  };
};
